// src/utils/purchaseOrderModel.js

// 📋 Purchase Order Schema
export const PurchaseOrderSchema = {
  _id: '',
  poNumber: '',
  requestingBranch: '',
  supplierType: 'supplier',
  supplier: '',
  warehouse: '',
  orderDate: '',
  expectedDeliveryDate: '',
  notes: '',
  orderItems: [],
  status: 'pending',
  createdBy: null,
  createdAt: null,
  updatedAt: null
}

// 🎯 Supplier Types
export const SUPPLIER_TYPES = {
  SUPPLIER: 'supplier',
  WAREHOUSE: 'warehouse'
}

// 📊 Purchase Order Status
export const PO_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
}

// ✅ Validation Rules
export const VALIDATION_RULES = {
  requestingBranch: {
    required: true,
    message: 'Please select a requesting branch'
  },
  supplierType: {
    required: true,
    enum: Object.values(SUPPLIER_TYPES),
    message: 'Please select a supplier type'
  },
  supplier: {
    required: true,
    message: 'Please select a supplier'
  },
  warehouse: {
    required: true,
    message: 'Please select a warehouse'
  },
  orderDate: {
    required: true,
    message: 'Order date is required'
  },
  expectedDeliveryDate: {
    required: true,
    message: 'Expected delivery date is required'
  },
  orderItems: {
    required: true,
    minLength: 1,
    message: 'At least one item is required'
  }
}

// 🔄 Purchase Order Model Utilities
export class PurchaseOrderModel {
  
  // Validate purchase order data
  static validate(orderData) {
    const errors = {}
    
    // Validate requesting branch
    if (!orderData.requestingBranch || !orderData.requestingBranch.trim()) {
      errors.requestingBranch = VALIDATION_RULES.requestingBranch.message
    }
    
    // Validate supplier type
    if (!orderData.supplierType) {
      errors.supplierType = VALIDATION_RULES.supplierType.message
    } else if (!VALIDATION_RULES.supplierType.enum.includes(orderData.supplierType)) {
      errors.supplierType = VALIDATION_RULES.supplierType.message
    }
    
    // Validate supplier or warehouse based on type
    if (orderData.supplierType === 'supplier') {
      if (!orderData.supplier || !orderData.supplier.trim()) {
        errors.supplier = VALIDATION_RULES.supplier.message
      }
    } else if (orderData.supplierType === 'warehouse') {
      if (!orderData.warehouse || !orderData.warehouse.trim()) {
        errors.warehouse = VALIDATION_RULES.warehouse.message
      }
    }
    
    // Validate order date
    if (!orderData.orderDate) {
      errors.orderDate = VALIDATION_RULES.orderDate.message
    } else {
      const orderDate = new Date(orderData.orderDate)
      if (isNaN(orderDate.getTime())) {
        errors.orderDate = 'Invalid order date'
      }
    }
    
    // Validate expected delivery date
    if (!orderData.expectedDeliveryDate) {
      errors.expectedDeliveryDate = VALIDATION_RULES.expectedDeliveryDate.message
    } else {
      const deliveryDate = new Date(orderData.expectedDeliveryDate)
      if (isNaN(deliveryDate.getTime())) {
        errors.expectedDeliveryDate = 'Invalid expected delivery date'
      } else if (orderData.orderDate) {
        const orderDate = new Date(orderData.orderDate)
        if (deliveryDate <= orderDate) {
          errors.expectedDeliveryDate = 'Expected delivery date must be after order date'
        }
      }
    }
    
    // Validate order items
    if (!orderData.orderItems || !Array.isArray(orderData.orderItems)) {
      errors.orderItems = VALIDATION_RULES.orderItems.message
    } else if (orderData.orderItems.length === 0) {
      errors.orderItems = VALIDATION_RULES.orderItems.message
    } else {
      // Validate each item
      orderData.orderItems.forEach((item, index) => {
        if (!item.quantity || item.quantity <= 0) {
          errors[`orderItems.${index}.quantity`] = 'Item quantity must be greater than 0'
        }
        if (!item.productId && !item.rawMaterialId) {
          errors[`orderItems.${index}.item`] = 'Item selection is required'
        }
      })
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Format data for API (CREATE)
  static formatForAPI(orderData) {
    const apiData = {
      requestingBranch: orderData.requestingBranch,
      supplierType: orderData.supplierType,
      orderDate: orderData.orderDate,
      expectedDeliveryDate: orderData.expectedDeliveryDate,
      orderItems: orderData.orderItems.map(item => ({
        itemType: item.itemType,
        productId: item.itemType === 'product' ? item.productId : undefined,
        rawMaterialId: item.itemType === 'raw_material' ? item.rawMaterialId : undefined,
        quantity: parseInt(item.quantity) || 1
      }))
    }

    // Add supplier or warehouse based on type
    if (orderData.supplierType === 'supplier') {
      apiData.supplier = orderData.supplier
    } else if (orderData.supplierType === 'warehouse') {
      apiData.warehouse = orderData.warehouse
    }

    // Add optional fields
    if (orderData.notes && orderData.notes.trim()) {
      apiData.notes = orderData.notes.trim()
    }

    return apiData
  }
  
  // Format data for API (UPDATE) - Only include updatable fields
  static formatForUpdate(orderData) {
    const updateData = {
      requestingBranch: orderData.requestingBranch,
      supplierType: orderData.supplierType,
      // Note: orderDate is excluded for updates as it's not allowed by the API
      expectedDeliveryDate: orderData.expectedDeliveryDate,
      orderItems: orderData.orderItems.map(item => ({
        itemType: item.itemType,
        productId: item.itemType === 'product' ? item.productId : undefined,
        rawMaterialId: item.itemType === 'raw_material' ? item.rawMaterialId : undefined,
        quantity: parseInt(item.quantity) || 1
      }))
    }

    // Add supplier or warehouse based on type
    if (orderData.supplierType === 'supplier') {
      updateData.supplier = orderData.supplier
    } else if (orderData.supplierType === 'warehouse') {
      updateData.warehouse = orderData.warehouse
    }

    // Add optional fields
    if (orderData.notes && orderData.notes.trim()) {
      updateData.notes = orderData.notes.trim()
    }

    return updateData
  }
  
  // Create default purchase order
  static createDefault() {
    return {
      ...PurchaseOrderSchema,
      orderDate: new Date().toISOString().split('T')[0],
      orderItems: []
    }
  }
  
  // Get status configuration
  static getStatusConfig(status) {
    const statusMap = {
      [PO_STATUS.PENDING]: {
        label: 'Pending',
        icon: '⏳',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      [PO_STATUS.APPROVED]: {
        label: 'Approved',
        icon: '✅',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      },
      [PO_STATUS.COMPLETED]: {
        label: 'Completed',
        icon: '🏁',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      [PO_STATUS.CANCELLED]: {
        label: 'Cancelled',
        icon: '❌',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      }
    }
    
    return statusMap[status] || {
      label: status || 'Unknown',
      icon: '❓',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-200'
    }
  }
}

export default PurchaseOrderModel